import React from "react";
import { Search, AlertTriangle, X } from "lucide-react";

/**
 * Filter bar for upcoming events lists. Renders inline (no border) so it
 * adapts to any parent container. State is fully controlled by the parent
 * (typically via the useEventFilters hook).
 *
 * `alertCount` is shown next to the "Alertes uniquement" toggle to nudge
 * users when alerts are present.
 */
export default function EventsFilterBar({
  search, setSearch,
  alertOnly, setAlertOnly,
  range, setRange,
  alertCount = 0,
  totalCount,
  filteredCount,
  className = "",
}) {
  const isFiltered = !!search || alertOnly || range !== "all";

  const clear = () => {
    setSearch("");
    setAlertOnly(false);
    setRange("all");
  };

  return (
    <div data-testid="events-filter-bar" className={`flex flex-wrap items-center gap-2 ${className}`}>
      {/* Search */}
      <div className="relative flex-1 min-w-[180px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C4664]" />
        <input
          type="search"
          data-testid="events-filter-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher (nom, hôte, code…)"
          className="w-full h-10 pl-9 pr-3 rounded-full border border-[#F2E2EC] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D81B8C]/40"
        />
      </div>

      {/* Date range */}
      <div className="inline-flex rounded-full border border-[#F2E2EC] bg-white p-0.5 text-xs" role="tablist">
        {[
          { id: "week",  label: "7j" },
          { id: "month", label: "30j" },
          { id: "all",   label: "Tous" },
        ].map((r) => {
          const active = range === r.id;
          return (
            <button
              key={r.id}
              type="button"
              data-testid={`events-filter-range-${r.id}`}
              onClick={() => setRange(r.id)}
              className={`h-9 px-3 rounded-full font-semibold transition-colors ${
                active ? "bg-[#2A0E3C] text-white" : "text-[#5C4664] hover:text-[#2A0E3C]"
              }`}
            >
              {r.label}
            </button>
          );
        })}
      </div>

      {/* Alert toggle */}
      <button
        type="button"
        data-testid="events-filter-alert-toggle"
        onClick={() => setAlertOnly((v) => !v)}
        className={`inline-flex items-center gap-1.5 h-10 px-3 rounded-full text-xs font-semibold transition-colors ${
          alertOnly
            ? "bg-red-600 text-white border border-red-700"
            : "bg-white text-red-700 border border-red-200 hover:bg-red-50"
        }`}
        title="N'afficher que les événements avec des cadeaux non stockés à J-48h"
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        Alertes
        {alertCount > 0 && (
          <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${
            alertOnly ? "bg-white text-red-700" : "bg-red-600 text-white"
          }`}>
            {alertCount}
          </span>
        )}
      </button>

      {/* Counts + clear */}
      <div className="ml-auto flex items-center gap-2 text-xs text-[#5C4664]">
        <span data-testid="events-filter-count">
          {filteredCount === totalCount
            ? `${totalCount} événement${totalCount > 1 ? "s" : ""}`
            : `${filteredCount} / ${totalCount}`}
        </span>
        {isFiltered && (
          <button
            type="button"
            onClick={clear}
            data-testid="events-filter-clear"
            className="inline-flex items-center gap-1 text-[#A60F6E] hover:underline"
          >
            <X className="w-3 h-3" /> Réinitialiser
          </button>
        )}
      </div>
    </div>
  );
}
